import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Toaster } from '../../components/ui/sonner';
import { toast } from 'sonner';
import { Shield, Mail, Lock, Loader2, Eye, EyeOff } from 'lucide-react';

const AdminLoginPage = () => {
    const { loginWithEmail, isAuthenticated, isAdmin, isSuperAdmin, isStaff } = useAuth();
    const navigate = useNavigate();
    
    const [showPassword, setShowPassword] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');

    // Redirect if already authenticated as admin/staff
    useEffect(() => {
        if (isAuthenticated) {
            if (isSuperAdmin) {
                navigate('/super-admin', { replace: true });
            } else if (isAdmin) {
                navigate('/admin', { replace: true });
            } else if (isStaff) {
                navigate('/staff', { replace: true });
            }
        }
    }, [isAuthenticated, isAdmin, isSuperAdmin, isStaff, navigate]);

    const handleLogin = async (e) => {
        e.preventDefault();
        
        if (!email || !password) {
            toast.error('Please fill in all fields');
            return;
        }
        
        setIsSubmitting(true);
        try {
            const user = await loginWithEmail(email, password);
            
            // Check if user has staff/admin access
            if (!['staff', 'admin', 'super_admin'].includes(user.role)) {
                toast.error('Access denied. Admin or staff credentials required.');
                return;
            }
            
            toast.success(`Welcome back, ${user.name}!`);
            
            if (user.role === 'super_admin') {
                navigate('/super-admin');
            } else if (user.role === 'admin') {
                navigate('/admin');
            } else {
                navigate('/staff');
            }
        } catch (err) {
            toast.error(err.message || 'Invalid credentials');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-neutral-900 via-neutral-800 to-neutral-900 p-4">
            <div className="w-full max-w-md">
                {/* Logo */}
                <div className="text-center mb-8">
                    <Link to="/" className="inline-flex items-center gap-2">
                        <div className="w-12 h-12 rounded-xl bg-primary-600 flex items-center justify-center">
                            <Shield className="w-7 h-7 text-white" />
                        </div>
                    </Link>
                    <h1 className="font-heading text-2xl font-bold text-white mt-4">
                        Admin Portal
                    </h1>
                    <p className="text-neutral-400 mt-1">
                        RecruitNearMe Administration
                    </p>
                </div>

                <Card className="bg-neutral-800/50 border-neutral-700">
                    <CardHeader>
                        <CardTitle className="text-white">Sign In</CardTitle>
                        <CardDescription className="text-neutral-400">
                            Enter your admin credentials to access the dashboard
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleLogin} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="admin-email" className="text-neutral-300">Email</Label>
                                <div className="relative">
                                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-500" />
                                    <Input
                                        id="admin-email"
                                        type="email"
                                        placeholder="admin@recruitnearme.co.uk"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="pl-10 h-12 bg-neutral-700/50 border-neutral-600 text-white placeholder:text-neutral-500"
                                        data-testid="admin-email-input"
                                    />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="admin-password" className="text-neutral-300">Password</Label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-500" />
                                    <Input
                                        id="admin-password"
                                        type={showPassword ? 'text' : 'password'}
                                        placeholder="Enter your password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="pl-10 pr-10 h-12 bg-neutral-700/50 border-neutral-600 text-white placeholder:text-neutral-500"
                                        data-testid="admin-password-input"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-neutral-300"
                                    >
                                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                    </button>
                                </div>
                            </div>
                            <Button
                                type="submit"
                                className="w-full h-12 bg-primary-600 hover:bg-primary-700 mt-6"
                                disabled={isSubmitting}
                                data-testid="admin-login-btn"
                            >
                                {isSubmitting ? (
                                    <>
                                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                        Signing in...
                                    </>
                                ) : (
                                    <>
                                        <Shield className="w-5 h-5 mr-2" />
                                        Access Admin Portal
                                    </>
                                )}
                            </Button>
                        </form>

                        <div className="mt-6 pt-6 border-t border-neutral-700">
                            <div className="text-center">
                                <p className="text-sm text-neutral-500 mb-3">Demo Credentials</p>
                                <div className="grid grid-cols-2 gap-3 text-xs">
                                    <div className="bg-neutral-700/30 rounded-lg p-3">
                                        <p className="text-amber-400 font-semibold mb-1">Super Admin</p>
                                        <p className="text-neutral-400">superadmin@recruitnearme.co.uk</p>
                                        <p className="text-neutral-400">SuperAdmin@123</p>
                                    </div>
                                    <div className="bg-neutral-700/30 rounded-lg p-3">
                                        <p className="text-blue-400 font-semibold mb-1">Admin Manager</p>
                                        <p className="text-neutral-400">admin@recruitnearme.co.uk</p>
                                        <p className="text-neutral-400">Admin@123</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <p className="text-center text-neutral-500 text-sm mt-6">
                    <Link to="/" className="text-primary-400 hover:text-primary-300">
                        ← Back to main site
                    </Link>
                </p>
            </div>
            <Toaster position="top-right" richColors />
        </div>
    );
};

export default AdminLoginPage;
